import axios from 'axios'

import { client } from '@/lib/api/client'
import {
  API_BASE_URL,
  APP_VERSION,
  AUTH_PATHS,
  DEFAULT_TIMEOUT_MS,
  LOGOUT_TIMEOUT_MS,
  OS_TYPE,
} from '@/lib/api/config'
import { getDeviceId } from '@/lib/api/deviceId'
import { toApiError } from '@/lib/api/errors'
import { attachApiResponseLogger } from '@/lib/api/responseLogger'
import { tokenStore } from '@/lib/api/tokenStore'

import { encodePassword } from './password'
import { readTokenBody } from './tokenBody'
import { readTokenHeaders } from './tokenHeaders'
import type {
  LoginInput,
  LoginVenueBrief,
  TokenView,
  VenueManagerAccount,
  VenueManagerLoginResult,
} from './types'

/**
 * 로그인 전용 클라이언트 — 토큰이 아직 없으므로 `client` 의 인터셉터를 태울 이유가 없다.
 * (`client` 를 써도 `isAuthFreePath` 로 토큰 주입은 건너뛰지만, 401 재시도 경로가 의미 없다.)
 */
const loginClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
})

attachApiResponseLogger(loginClient)

function readAccount(body: unknown): VenueManagerAccount | null {
  if (typeof body !== 'object' || body === null) return null
  const manager = (body as { manager?: unknown }).manager
  if (typeof manager !== 'object' || manager === null) return null

  const record = manager as Record<string, unknown>
  const memberId = typeof record.memberId === 'string' ? record.memberId : null
  if (!memberId) return null

  const optional = (key: string): string | null => {
    const value = record[key]
    return typeof value === 'string' && value.length > 0 ? value : null
  }

  return {
    memberId,
    name: optional('name'),
    nickname: optional('nickname'),
    email: optional('email'),
  }
}

function readVenues(body: unknown): LoginVenueBrief[] {
  if (typeof body !== 'object' || body === null) return []
  const venues = (body as { venues?: unknown }).venues
  if (!Array.isArray(venues)) return []

  return venues.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const record = entry as Record<string, unknown>
    if (
      typeof record.venueId !== 'string' ||
      !/^[1-9]\d*$/.test(record.venueId) ||
      typeof record.name !== 'string'
    ) {
      return []
    }
    return [{ venueId: record.venueId, name: record.name }]
  })
}

/**
 * `POST /venue/v1/manager/auth/login` — 구장 관리자 전용 로그인 (#583 §2-2(4)①).
 *
 * - 🚨 **비밀번호는 Base64** 로 보낸다. 평문을 보내면 400 `INVALID_PASSWORD_ENCODING` 이다(설계 R1).
 * - 🚨 식별자는 이메일이 아니라 `loginId` 다(#583 D2).
 * - 토큰은 **바디 우선, 헤더 폴백**으로 읽는다(설계 D1). 바디는 ISO, 헤더는 epoch millis 라
 *   형식이 달라 변환 지점을 `tokenBody.ts` 하나로 가둔다.
 * - `Os-Type`·`App-Version` 은 이 EP 가 요구하지 않지만 계속 보낸다(설계 D2 — 비용 0, T3 재발 차단).
 * - `fcmToken` 은 새 요청 DTO 에 없으므로 보내지 않는다.
 */
export async function login(input: LoginInput): Promise<VenueManagerLoginResult> {
  let response
  try {
    response = await loginClient.post(
      AUTH_PATHS.login,
      {
        loginId: input.loginId,
        password: encodePassword(input.password),
        deviceId: getDeviceId(),
      },
      { headers: { 'Os-Type': OS_TYPE, 'App-Version': APP_VERSION } }
    )
  } catch (error) {
    throw toApiError(error)
  }

  const tokens: TokenView | null =
    readTokenBody(response.data) ?? readTokenHeaders(response.headers)
  if (!tokens) {
    throw toApiError(
      new Error('로그인 응답에서 토큰을 읽지 못했습니다. 잠시 후 다시 시도해 주세요.')
    )
  }
  if (!tokenStore.saveTokens(tokens)) {
    throw toApiError(
      new Error('로그인 정보를 저장하지 못했습니다. 브라우저 저장소 설정을 확인해 주세요.')
    )
  }

  return { tokens, manager: readAccount(response.data), venues: readVenues(response.data) }
}

/**
 * `POST /venue/v1/manager/auth/logout` — 그 디바이스의 서버 refresh 행을 무효화한다 (#583 §2-2(4)③).
 *
 * 🚨 **`client` 를 경유한다**(불변식 2) — 이 EP 는 `authenticated()` 라 Bearer 가 필요하고,
 * principal 의 `memberId` 와 `deviceId` 로 행을 찾는다. 토큰을 먼저 지운 뒤 부르면 401 이 되어
 * **서버 세션이 만료까지 살아남는다**(설계 D7).
 *
 * 🚨 액세스 토큰은 stateless 라 즉시 무효화되지 않는다(#583 S5). 로컬 정리는 호출부(`clearSession`)가 한다.
 */
export async function logout(): Promise<void> {
  try {
    await client.post(
      AUTH_PATHS.logout,
      { deviceId: getDeviceId() },
      { timeout: LOGOUT_TIMEOUT_MS }
    )
  } catch (error) {
    throw toApiError(error)
  }
}
